import { useMemo } from "react";

import type { Margin } from "~/v2/api/rest/account";
import { parseMoney } from "../overview.format";
import type { BalanceSection } from "../types";

export interface UseAccountBalancesCardControllerParams {
  margin: Margin | undefined;
}

export interface AccountBalancesCardControllerValue {
  sections: BalanceSection[];
}

export const useAccountBalancesCardController = ({
  margin,
}: UseAccountBalancesCardControllerParams): AccountBalancesCardControllerValue => {
  const sections = useMemo<BalanceSection[]>(
    () => [
      {
        title: "Buying power",
        rows: [
          {
            label: "Buying Power",
            tooltip: "Buying power for marginable assets.",
            value: parseMoney(margin?.buying_power),
          },
          {
            label: "Options Buying Power",
            tooltip: "Buying power for options trading.",
            value: parseMoney(margin?.options_buying_power),
          },
          {
            label: "Non-Marginable Buying Power",
            tooltip:
              "Buying power for assets that cannot be purchased on margin, such as crypto.",
            value: parseMoney(margin?.non_marginable_buying_power),
          },
        ],
      },
      {
        title: "Equity",
        rows: [
          {
            label: "Total Equity",
            tooltip:
              "Total account value: cash plus the market value of all positions (long and short).",
            value: parseMoney(margin?.equity),
            emphasize: true,
          },
          {
            label: "Long Market Value",
            tooltip: "Current market value of all long positions.",
            value: parseMoney(margin?.long_market_value),
          },
          {
            label: "Short Market Value",
            tooltip: "Current market value of all short positions.",
            value: parseMoney(margin?.short_market_value),
            signed: true,
          },
        ],
      },
      {
        title: "Margin",
        rows: [
          {
            label: "Initial Margin",
            tooltip:
              "Reg T initial margin requirement for your open positions. Updates as prices and positions change.",
            value: parseMoney(margin?.initial_margin),
          },
          {
            label: "Maintenance Margin",
            tooltip:
              "Minimum equity required to keep your current positions open. If equity falls below this, you may face a margin call.",
            value: parseMoney(margin?.maintenance_margin),
          },
        ],
      },
    ],
    [margin],
  );

  return { sections };
};
